import { useState, useEffect, useCallback, useRef } from "react";

/**
 * Calculates the rendered position of a background image using object-fit: cover math.
 * Returns a function to convert image-relative percentages to viewport pixel positions.
 * This ensures overlay elements stay locked to the image regardless of viewport size.
 */

interface ImageRect {
  offsetX: number;
  offsetY: number;
  renderW: number;
  renderH: number;
}

interface OverlayBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface ImageBoxPosition {
  /** X position as percentage of image width (0-100) */
  imgLeft: number;
  /** Y position as percentage of image height (0-100) */
  imgTop: number;
  /** Width as percentage of image width (0-100) */
  imgWidth: number;
  /** Height as percentage of image height (0-100) */
  imgHeight: number;
}

export function useImageOverlay(imageSrc: string) {
  const [imageRect, setImageRect] = useState<ImageRect>({
    offsetX: 0,
    offsetY: 0,
    renderW: 0,
    renderH: 0,
  });
  const naturalSize = useRef({ w: 0, h: 0 });
  const loaded = useRef(false);

  const calculate = useCallback(() => {
    if (!naturalSize.current.w) return;

    const cW = window.innerWidth;
    const cH = window.innerHeight;
    const iW = naturalSize.current.w;
    const iH = naturalSize.current.h;
    const cRatio = cW / cH;
    const iRatio = iW / iH;

    let renderW: number, renderH: number, offsetX: number, offsetY: number;

    if (cRatio > iRatio) {
      // Viewport is wider than image — image fills width, overflows height
      renderW = cW;
      renderH = cW / iRatio;
      offsetX = 0;
      offsetY = (cH - renderH) / 2;
    } else {
      // Viewport is taller than image — image fills height, overflows width
      renderH = cH;
      renderW = cH * iRatio;
      offsetX = (cW - renderW) / 2;
      offsetY = 0;
    }

    setImageRect({ offsetX, offsetY, renderW, renderH });
  }, []);

  useEffect(() => {
    if (loaded.current) return;
    const img = new Image();
    img.onload = () => {
      naturalSize.current = { w: img.naturalWidth, h: img.naturalHeight };
      loaded.current = true;
      calculate();
    };
    img.src = imageSrc;
  }, [imageSrc, calculate]);

  useEffect(() => {
    window.addEventListener("resize", calculate);
    return () => window.removeEventListener("resize", calculate);
  }, [calculate]);

  /** Convert image-relative percentage box to viewport pixel box */
  const getBoxPixels = useCallback(
    (box: ImageBoxPosition): OverlayBox => {
      const { offsetX, offsetY, renderW, renderH } = imageRect;
      return {
        left: offsetX + (box.imgLeft / 100) * renderW,
        top: offsetY + (box.imgTop / 100) * renderH,
        width: (box.imgWidth / 100) * renderW,
        height: (box.imgHeight / 100) * renderH,
      };
    },
    [imageRect]
  );

  return { imageRect, getBoxPixels, ready: imageRect.renderW > 0 };
}
